import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import OperationalHealthBanner from './OperationalHealthBanner';
import SupervisorActionQueue from './SupervisorActionQueue';
import ExpandedAlertsPanel from './ExpandedAlertsPanel';
import DailyNotesPanel from './DailyNotesPanel';
import { ActionQueueItem } from '../../utils/operationsDashboardUtils';
import { Campaign } from '../../types';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface CommandCenterPanelProps {
  operationalHealth?: { level: string; message: string; icon?: string };
  lastUpdated?: string;
  actionQueue: ActionQueueItem[];
  alerts: any[];
  rows: any[];
  dailyNotes: any[];
  date: string;
  campaignId?: string;
  campaigns: Campaign[];
  canManage: boolean;
  canNotify: boolean;
  findRowForAlert: (alert: any) => any;
  onActionQueueItem: (item: ActionQueueItem) => void;
  onAssignCoverage: (row: any) => void;
  onNotRequired: (row: any) => void;
  onViewEmployee: (row: any) => void;
  onRefreshNotes: () => void;
  onSendSummary: () => void;
  onRefresh: () => void;
  density?: DashboardDensity;
}

const CommandCenterPanel: React.FC<CommandCenterPanelProps> = (props) => {
  const [tab, setTab] = useState<'actions' | 'alerts' | 'notes'>('actions');
  const [panelOpen, setPanelOpen] = useState(false);
  const density = props.density || 'standard';
  const compact = isCompact(density);

  const handleTabChange = (_: React.SyntheticEvent, v: 'actions' | 'alerts' | 'notes') => {
    if (compact) {
      if (v === tab && panelOpen) {
        setPanelOpen(false);
      } else {
        setTab(v);
        setPanelOpen(true);
      }
    } else {
      setTab(v);
    }
  };

  const panelContent = (
    <>
      {tab === 'actions' && (
        <Stack spacing={compact ? 0.5 : 1}>
          <OperationalHealthBanner
            health={props.operationalHealth}
            lastUpdated={props.lastUpdated}
            density={density}
          />
          <SupervisorActionQueue
            items={props.actionQueue}
            onAction={props.onActionQueueItem}
            density={density}
            embedded
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {props.canNotify && (
              <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.25 }} onClick={props.onSendSummary}>
                Send Summary
              </Button>
            )}
            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.25 }} onClick={props.onRefresh}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      )}

      {tab === 'alerts' && (
        <ExpandedAlertsPanel
          alerts={props.alerts}
          rows={props.rows}
          canManage={props.canManage}
          findRowForAlert={props.findRowForAlert}
          onAssignCoverage={props.onAssignCoverage}
          onNotRequired={props.onNotRequired}
          onViewEmployee={props.onViewEmployee}
          density={density}
          embedded
        />
      )}

      {tab === 'notes' && (
        <DailyNotesPanel
          notes={props.dailyNotes}
          date={props.date}
          campaignId={props.campaignId}
          campaigns={props.campaigns}
          canManage={props.canManage}
          onRefresh={props.onRefreshNotes}
          density={density}
          embedded
        />
      )}
    </>
  );

  if (compact) {
    return (
      <Box
        sx={{
          mb: 0.75,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              flex: 1,
              '& .MuiTab-root': { minHeight: 36, py: 0.25, fontSize: '0.7rem', fontWeight: 600, minWidth: 0, px: 1.5 },
            }}
          >
            <Tab value="actions" label={`Actions (${props.actionQueue.length})`} />
            <Tab value="alerts" label={`Alerts (${props.alerts.length})`} />
            <Tab value="notes" label={`Notes (${props.dailyNotes.length})`} />
          </Tabs>
          {panelOpen && (
            <Button
              size="small"
              onClick={() => setPanelOpen(false)}
              sx={{ fontSize: '0.65rem', minWidth: 0, mr: 0.5, textTransform: 'none' }}
            >
              Close
            </Button>
          )}
        </Stack>
        <Collapse in={panelOpen} timeout="auto">
          <Box sx={{ p: 0.75, borderTop: '1px solid', borderColor: 'divider', maxHeight: 240, overflowY: 'auto' }}>
            {panelContent}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: { lg: 'sticky' },
        top: 64,
        maxHeight: { lg: 'calc(100vh - 72px)' },
        overflowY: { lg: 'auto' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.75rem', fontWeight: 600 },
        }}
      >
        <Tab value="actions" label={`Actions (${props.actionQueue.length})`} />
        <Tab value="alerts" label={`Alerts (${props.alerts.length})`} />
        <Tab value="notes" label="Notes" />
      </Tabs>

      <Box sx={{ p: 1 }}>
        {panelContent}
      </Box>
    </Box>
  );
};

export default CommandCenterPanel;
